import type { SfdcStep } from 'clickim/src/common/models/sfdcStep';
import type { SleepStep } from 'clickim/src/common/models/sleepStep';
import type { StepPlayback } from 'clickim/src/common/playback/stepPlayers/stepPlayback';
import type { IPlaybackTimeoutCalculator } from 'clickim/src/background/playbackTimeoutCalculator';

const COMMUNICATION_BUFFER_TIME = 1000;
const UI_VERIFICATION_STEPS = ['simple-ui-verification', 'wait-for-simple-ui-verification'];
const FULL_TIMEOUT_STEP_TYPES = [
    ...UI_VERIFICATION_STEPS,
    'custom-validation',
    'sfdc-internal-test-step',
    'sfdc-recorded-step',
    'sfdc-step-login',
    'sfdc-step-loginas',
    'sfdc-step-logout',
    'sfdc-step-sobjectcreate',
    'sfdc-step-sobjectdelete',
    'sfdc-step-findrecord',
    'sfdc-step-quickaction',
    'sfdc-step-sobjectvalidate',
    'sfdc-step-launchapp',
    'sfdc-step-wait-for-page-load',
    'sfdc-step-closeconsoletabs',
    'sfdc-step-sobjectedit',
    'sfdc-step-relatedlistaction',
    'sfdc-step-permission-validation',
    'sfdc-step-convert-lead-to-opportunity',
    'sfdc-step-quotelineeditor',
    'sfdc-document-validation',
    'sfdc-step-flow-screen-completion',
    'sfdc-step-sobjectverifyoptions',
];

interface RetryTimeReport {
    tab?: number;
    frame?: number;
    condition?: number;
    'pre-locate'?: number;
    locate?: number;
    action?: number;
}

export class PlaybackTimeoutCalculator implements IPlaybackTimeoutCalculator {
    private totalStepTime: number = 0;
    public currentRetryStart = 0;
    private lastUpdateTime: number | undefined;
    private totalStepTimesReport: RetryTimeReport[] = [];
    private currentRetryTimes: number[] = [];
    private currentRetryTimesReport: RetryTimeReport = {};

    constructor(private readonly isDebuggerConnected: boolean) {
        this.resetStepVariables();
        this.resetRetryVariables();
    }

    private resetStepVariables(totalStepTime?: number, currentRetryTimes?: number[]) {
        this.currentRetryTimes = currentRetryTimes || [];
        this.totalStepTime = totalStepTime || 0;
        this.totalStepTimesReport = [];
        this.currentRetryTimesReport = {};
        const now = Date.now();
        this.currentRetryStart = now;
        this.lastUpdateTime = now;
    }

    private resetRetryVariables() {
        const now = Date.now();
        this.currentRetryStart = now;
        this.lastUpdateTime = now;
        this.totalStepTimesReport.push(this.currentRetryTimesReport);
        this.currentRetryTimesReport = {};
    }

    public initStepRun(stepPlayback: StepPlayback) {
        const getRetryTimeoutSuggestions = (totalStepTime: number) => {
            const timeToPlayStep = this.getTotalStepTimeLeftToPlay(stepPlayback, totalStepTime);
            const MINIMAL_RETRY_TIME = 5000;
            if (timeToPlayStep <= MINIMAL_RETRY_TIME) {
                return [MINIMAL_RETRY_TIME];
            }
            return [Math.max(MINIMAL_RETRY_TIME, timeToPlayStep / 3)];
        };
        stepPlayback.setStartTimestamp();
        const totalStepTime = this.getTotalStepRunTime(stepPlayback);
        const currentRetryTimes = FULL_TIMEOUT_STEP_TYPES.includes(stepPlayback.stepType) ? [totalStepTime] : getRetryTimeoutSuggestions(totalStepTime);
        this.resetStepVariables(totalStepTime, currentRetryTimes);
        stepPlayback.context.data.maxTotalStepTime = totalStepTime;
    }

    public initRetryTime() {
        this.resetRetryVariables();
    }

    private getTotalStepRunTime(stepPlayback: StepPlayback) {
        const HALF_HOUR_IN_MS = 30 * 60 * 1000;
        let fallbackTimeout = stepPlayback.context.config.stepTimeout;
        if (UI_VERIFICATION_STEPS.includes(stepPlayback.stepType)) {
            fallbackTimeout = stepPlayback.context.config.applitoolsStepTimeout || HALF_HOUR_IN_MS;
        }
        if (stepPlayback.step.type.startsWith('sfdc-')) {
            fallbackTimeout = (stepPlayback.step as SfdcStep).defaultTimeout;
        }
        return (stepPlayback.step.useStepTimeout && stepPlayback.step.stepTimeout) ? stepPlayback.step.stepTimeout : fallbackTimeout;
    }

    public getTotalStepTimeLeftToPlay(stepPlayback: StepPlayback, totalStepTime = this.totalStepTime) {
        const playTimeSoFar = Date.now() - stepPlayback.startTimestamp!;
        return totalStepTime - playTimeSoFar;
    }

    private getCurrentRetryTime(stepPlayback: StepPlayback) {
        return (stepPlayback.retryIndex! < this.currentRetryTimes.length) ?
            this.currentRetryTimes[stepPlayback.retryIndex!] :
            this.getTotalStepTimeLeftToPlay(stepPlayback);
    }

    private getTotalCurrentRetryTimeLeft(stepPlayback: StepPlayback) {
        const totalRetryTime = Date.now() - this.currentRetryStart;
        return this.getCurrentRetryTime(stepPlayback) - totalRetryTime + COMMUNICATION_BUFFER_TIME;
    }

    public getTabTimeout(stepPlayback: StepPlayback) {
        return this.getTotalCurrentRetryTimeLeft(stepPlayback);
    }

    public getDynamicParentTimeout(stepPlayback: StepPlayback) {
        return this.getTotalCurrentRetryTimeLeft(stepPlayback);
    }

    public getFrameTimeout(stepPlayback: StepPlayback) {
        return this.getTotalCurrentRetryTimeLeft(stepPlayback);
    }

    public getLocateTimeout(stepPlayback: StepPlayback) {
        return this.getTotalCurrentRetryTimeLeft(stepPlayback);
    }

    public getActionTimeout(stepPlayback: StepPlayback) {
        if (this.isDebuggerConnected) {
            return 6e5;
        }
        const SLEEP_ERROR_MARGIN_MS = 5000;
        const actionType = stepPlayback.step.type;
        const MIN_ACTION_PLAYBACK_TIME = 30000;

        if (actionType === 'sleep') {
            return (stepPlayback.step as SleepStep).durationMS + SLEEP_ERROR_MARGIN_MS;
        }

        return Math.max(this.getTotalStepTimeLeftToPlay(stepPlayback), MIN_ACTION_PLAYBACK_TIME);
    }
}
